import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const errMsg = (e) => e?.response?.data?.detail || e.message;
const inp = "h-10 rounded-xl border border-stone-200 px-3 text-sm";

function NewBuilding({ onDone }) {
  const [f, setF] = useState({ name: "", address: "", lifts: "A, B", secName: "", secPhone: "", rescName: "", rescPhone: "" });
  const submit = async (e) => {
    e.preventDefault();
    const contacts = [
      f.secName && f.secPhone && { role: "security", name: f.secName, phone: f.secPhone },
      f.rescName && f.rescPhone && { role: "lift_rescue", name: f.rescName, phone: f.rescPhone },
    ].filter(Boolean);
    try {
      await api.post("/admin/sos/buildings", { name: f.name, address: f.address || undefined,
        lifts: f.lifts.split(",").map((x) => x.trim()).filter(Boolean), contacts });
      toast.success("Building saved"); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const field = (k, ph) => <input className={inp} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sosadm-${k}`} />;
  return (
    <form onSubmit={submit} className="rounded-2xl border border-stone-200 bg-white p-4 grid sm:grid-cols-2 gap-2" data-testid="sosadm-new">
      {field("name", "Building / society name")}{field("address", "Address (optional)")}
      {field("lifts", "Lift labels, comma separated")}<span />
      {field("secName", "Security desk name")}{field("secPhone", "Security desk phone")}
      {field("rescName", "Lift rescue contact")}{field("rescPhone", "Lift rescue phone")}
      <button className="sm:col-span-2 h-10 rounded-full bg-stone-900 text-white font-bold" data-testid="sosadm-create">Create building</button>
    </form>
  );
}

const printStickers = (id) => {
  document.getElementById("f2n-print-root")?.remove();
  const root = document.createElement("div");
  root.id = "f2n-print-root";
  root.className = "f2n-print";
  root.innerHTML = document.getElementById(`sos-print-${id}`).innerHTML;
  document.body.appendChild(root);
  window.addEventListener("afterprint", () => root.remove(), { once: true });
  window.print();
};


function Building({ b, onChange }) {
  const [lift, setLift] = useState("");
  const base = window.location.origin;
  const addLift = async () => {
    try { await api.patch(`/admin/sos/buildings/${b.building_id}`, { add_lifts: [lift] }); setLift(""); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rotate = async (l) => {
    if (!window.confirm(`Replace the QR for lift ${l.label}? The printed sticker will stop working.`)) return;
    try { await api.post(`/admin/sos/buildings/${b.building_id}/lifts/${l.lift_id}/rotate-qr`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid={`sosadm-bld-${b.building_id}`}>
      <div className="font-black">{b.name} <span className="text-xs text-stone-500 font-normal">{b.address}</span></div>
      <div className="text-xs text-stone-600">{b.contacts.length ? b.contacts.map((c) => `${c.role === "security" ? "Security" : "Lift rescue"}: ${c.name} ${c.phone}`).join(" · ") : "No desk contacts configured"}</div>
      <div id={`sos-print-${b.building_id}`} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {b.lifts.map((l) => {
          const url = `${base}/sos/lift?qr=${l.qr_code}`;
          return (
            <div key={l.lift_id} className="rounded-xl border border-stone-200 p-2 text-center space-y-1" data-testid={`sosadm-lift-${l.lift_id}`}>
              <QRCodeSVG value={url} size={96} className="mx-auto" />
              <div className="text-sm font-bold">Lift {l.label}</div>
              <div className="f2n-print-only text-[10px] font-bold">{b.name} · Scan for Lift SOS</div>
              <div className="text-[10px] text-stone-400 break-all" data-testid={`sosadm-qr-${l.lift_id}`}>{l.qr_code}</div>
              <button onClick={() => rotate(l)} className="text-[11px] underline text-red-700" data-testid={`sosadm-rotate-${l.lift_id}`}>Replace QR</button>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input className={inp} placeholder="New lift label" value={lift} onChange={(e) => setLift(e.target.value)} data-testid={`sosadm-addlift-${b.building_id}`} />
        <button disabled={!lift.trim()} onClick={addLift} data-testid={`sosadm-addlift-btn-${b.building_id}`} className="h-10 px-4 rounded-full border border-stone-300 font-bold disabled:opacity-50">Add lift</button>
        <button onClick={() => printStickers(b.building_id)} data-testid={`sosadm-print-${b.building_id}`} className="ms-auto h-10 px-4 rounded-full border border-stone-300 font-bold">Print stickers</button>
      </div>
    </div>
  );
}

export default function AdminSos() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => api.get("/admin/sos/buildings").then((r) => setItems(r.data.items))
    .catch((e) => { if (e?.response?.status === 403 || e?.response?.status === 401) setDenied(true); else toast.error(errMsg(e)); }), []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);
  if (!user || !user.is_admin || denied) {
    return <div className="max-w-xl mx-auto p-6 font-bold text-stone-700" data-testid="sosadm-denied">Admins only.</div>;
  }
  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="admin-sos-page">
      <h1 className="text-2xl font-black">Lift SOS · buildings &amp; QR codes</h1>
      <p className="text-xs text-stone-500">Each QR opens /sos/lift for that lift. SMS to desk contacts is SIMULATED until an SMS provider is approved and configured.</p>
      <NewBuilding onDone={load} />
      {items === null ? <p>Loading…</p> : items.length === 0 ? <p className="text-sm text-stone-500" data-testid="sosadm-empty">No buildings yet.</p>
        : items.map((b) => <Building key={b.building_id} b={b} onChange={load} />)}
    </div>
  );
}
